/**
 * TanStack Query client configuration (Block 6.A / ADR-024 D1 + D6).
 *
 * Defaults tuned for read-heavy directory surfaces:
 *   - staleTime: 30s. Within 30s of a successful fetch, navigation back
 *     to the same route serves cached data instantly. After 30s, fetch
 *     happens in background and renders fresh data when ready (stale-
 *     while-revalidate).
 *   - retry: shouldRetryRead / readRetryDelay. Network blips retry twice
 *     with TanStack's default delay; structural failures (4xx) never
 *     retry. A busy database (503 database_busy) is retried once, later,
 *     at a time of its own (build-specs/busy-says-so-and-retries-once.md).
 *   - refetchOnWindowFocus: true. Coming back to the tab refreshes data;
 *     the directory is shared between operators and stale data is the
 *     wrong default.
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, authExpiry, isDatabaseBusy } from './api';

const MAX_READ_RETRIES = 2;
const BUSY_DELAY_MIN_MS = 10_000;
const BUSY_DELAY_SPREAD_MS = 10_000;

/**
 * Whether a failed read is asked again. TanStack passes the 0-based
 * failure count. A busy database is retried only when it is the fetch's
 * first failure: after any earlier failure the pool has had its chance,
 * and the busy copy shows. Every other failure keeps the old rule: never
 * a 4xx, otherwise up to two retries.
 */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  if (isDatabaseBusy(error)) return failureCount === 0;
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < MAX_READ_RETRIES;
}

/**
 * How long to wait before asking again. A busy database waits between 10
 * and 20 seconds, drawn at random so the people who failed together do
 * not retry together. Everything else keeps TanStack's default (1 s,
 * then 2 s).
 */
export function readRetryDelay(failureCount: number, error: unknown): number {
  if (isDatabaseBusy(error)) return BUSY_DELAY_MIN_MS + Math.random() * BUSY_DELAY_SPREAD_MS;
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

export const queryClient = new QueryClient({
  // A read that rejects with a TypeError is either a network outage or a
  // lapsed session whose sign-in redirect the browser followed across
  // origins. Ask the platform which; a lapsed session goes to sign-in
  // instead of leaving an error on the page.
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof TypeError) void authExpiry.redirectIfSignedOut();
    },
  }),
  // The backstop for writes: every write helper surfaces an expired
  // sign-in itself (src/lib/writeFetch.ts), but a write that followed the
  // sign-in redirect anyway rejects with a TypeError. Ask the same
  // question; a lapsed session keeps the page's work and goes to sign-in.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof TypeError) void authExpiry.redirectIfSignedOut(window.location.pathname);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: shouldRetryRead,
      retryDelay: readRetryDelay,
      refetchOnWindowFocus: true,
    },
  },
});
